import React, {useEffect, useRef} from 'react';
import {Animated, Image, ImageSourcePropType} from 'react-native';
import {useTheme} from 'styled-components/native';
import {RFValue} from 'react-native-responsive-fontsize';

import {Wrapper, Track, GhostFill, Badge} from './styles';

interface BadgeProgressBarProps {
  /** Progresso de 0 a 1 */
  progress: number;
  /** Espessura da barra (equivalente ao height em RFValue) */
  height?: number;
  /** Imagem exibida no badge ao final da barra (ex: próximo item a desbloquear) */
  badgeImage?: ImageSourcePropType;
  /** Tamanho do círculo/fundo do badge */
  badgeSize?: number;
  /** Tamanho da imagem (carro) dentro do badge. Padrão: mesmo tamanho do badgeSize */
  carImageSize?: number;
  /** Cor de fundo do badge. Padrão: theme.colors.shape */
  badgeBackgroundColor?: string;
  /** Cor da borda/brilho do badge. Padrão: theme.colors.gold */
  badgeBorderColor?: string;
  /** Cor do preenchimento. Padrão: theme.colors.primary */
  fillColor?: string;
  /** Preenchimento mínimo visível quando progress é 0, para a barra não ficar "vazia" */
  minFillPercent?: number;
  /** Duração da animação em ms */
  duration?: number;
  /** Se falso, aplica o progresso instantaneamente, sem animação */
  animated?: boolean;
  style?: React.ComponentProps<typeof Wrapper>['style'];
}

export function BadgeProgressBar({
  progress,
  height = 20,
  badgeImage,
  badgeSize = 58,
  carImageSize,
  badgeBackgroundColor,
  badgeBorderColor,
  fillColor,
  minFillPercent = 0.1,
  duration = 800,
  animated = true,
  style,
}: BadgeProgressBarProps) {
  const theme = useTheme();
  const animatedPercent = useRef(new Animated.Value(0)).current;

  const targetPercent = progress <= 0 ? minFillPercent : Math.min(progress, 1);

  useEffect(() => {
    if (animated) {
      Animated.timing(animatedPercent, {
        toValue: targetPercent,
        duration,
        useNativeDriver: false,
      }).start();
    } else {
      animatedPercent.setValue(targetPercent);
    }
  }, [targetPercent, animated]);

  const width = animatedPercent.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <Wrapper style={style}>
      <Track height={height}>
        <GhostFill />
        <Animated.View
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width,
            backgroundColor: fillColor ?? theme.colors.primary,
            borderRadius: RFValue(height / 2),
          }}
        />
      </Track>

      {badgeImage && (
        <Badge
          size={badgeSize}
          backgroundColor={badgeBackgroundColor}
          borderColor={badgeBorderColor}>
          <Image
            source={badgeImage}
            style={{
              width: RFValue(carImageSize ?? badgeSize),
              height: RFValue(carImageSize ?? badgeSize),
            }}
            resizeMode="contain"
          />
        </Badge>
      )}
    </Wrapper>
  );
}
